import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { CreateCourseLessonRequest } from '../../../../../../lib/interfaces/courses';
import { Button } from '../../../../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../../../../ui/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../../../ui/components/ui/form';
import { Input } from '../../../../../../ui/components/ui/input';
import RichTextEditor, { ImageUploadHandler } from '../../../../../../ui/components/ui/rich-text-editor';
import { serializeLessonHtml } from '../../../../../../ui/components/ui/lesson-image-extension';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../../../ui/components/ui/tooltip';
import LessonView from '../../../../components/lesson-view/lesson-view';

/**
 * Mirrors the server rule: a lesson needs at least a video or text — an empty editor counts as no
 * content. An image is content even with no words, so it is checked before the tags are stripped.
 */
const hasEditorContent = (html: string) =>
  /<img\b/i.test(html) ||
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

const lessonFormSchema = z
  .object({
    title: z.string().trim().min(2, 'Mindestens 2 Zeichen').max(120, 'Maximal 120 Zeichen'),
    heading: z.string().trim().min(2, 'Mindestens 2 Zeichen').max(160, 'Maximal 160 Zeichen'),
    videoUrl: z.string().trim().url('Bitte eine gültige URL angeben (inkl. https://)').or(z.literal('')),
    textHtml: z.string()
  })
  .refine((data) => data.videoUrl !== '' || hasEditorContent(data.textHtml), {
    message: 'Eine Lektion benötigt entweder ein Video und / oder einen Text.',
    path: ['textHtml']
  });

type LessonFormValues = z.infer<typeof lessonFormSchema>;

export interface LessonFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Parent module — shown in the preview breadcrumb. */
  moduleTitle: string;
  /** Explicit — `lesson` is still null while an edit's content loads, so it can't carry the mode. */
  isEdit?: boolean;
  /** Content of the lesson being edited; null in create mode and while loading. */
  lesson?: { title: string; heading: string; videoUrl: string | null; textHtml: string | null } | null;
  isLoadingLesson?: boolean;
  /** Uploads an image used inside the body; when omitted the editor shows no image button. */
  onImageUpload?: ImageUploadHandler;
  onSubmit: (data: CreateCourseLessonRequest) => Promise<void>;
}

/** Label with an info affordance — keeps the field explanations out of the form's visual flow. */
const LabelWithHint = ({ children, hint }: { children: React.ReactNode; hint: string }) => (
  <FormLabel className="text-[14px] font-bold text-[#12282A] flex items-center gap-1.5">
    {children}
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label={hint} className="text-[#888] hover:text-[#12282A] transition-colors">
          <Info className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="rounded-lg max-w-[260px]">{hint}</TooltipContent>
    </Tooltip>
  </FormLabel>
);

const EMPTY: LessonFormValues = { title: '', heading: '', videoUrl: '', textHtml: '' };

const buildDefaults = (lesson: LessonFormDialogProps['lesson']): LessonFormValues =>
  lesson ? { title: lesson.title, heading: lesson.heading, videoUrl: lesson.videoUrl ?? '', textHtml: lesson.textHtml ?? '' } : EMPTY;

const LessonFormDialog = ({ isOpen, onOpenChange, moduleTitle, isEdit, lesson, isLoadingLesson, onImageUpload, onSubmit }: LessonFormDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<LessonFormValues>({
    resolver: zodResolver(lessonFormSchema),
    defaultValues: EMPTY
  });

  // Reset on open and once the edited lesson's content arrives.
  useEffect(() => {
    if (!isOpen) return;
    form.reset(buildDefaults(lesson));
  }, [isOpen, lesson, form]);

  const handleSubmit = async (values: LessonFormValues) => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        title: values.title.trim(),
        heading: values.heading.trim(),
        videoUrl: values.videoUrl.trim() || undefined,
        // Rewrites each image's signed src back to its stored path — the form contract to the server.
        textHtml: hasEditorContent(values.textHtml) ? serializeLessonHtml(values.textHtml) : undefined
      });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isSubmitting || !!isLoadingLesson;

  // Drives the preview — re-renders on every keystroke so it always shows the current draft.
  const watched = form.watch();

  /**
   * Leaving the title fills an untouched heading with it. Only ever fills a blank field, so a custom
   * heading survives later title edits.
   */
  const prefillHeadingFromTitle = () => {
    const title = form.getValues('title').trim();
    if (!title || form.getValues('heading').trim()) return;
    form.setValue('heading', title, { shouldValidate: true });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[1100px] min-w-[1100px] max-h-[88vh] py-4 px-6 gap-4 bg-white flex flex-col overflow-hidden"
        aria-describedby={undefined}
        // Radix focuses the first focusable child, which is the label's info button — that pops its
        // tooltip open on every mount. A form should start in its first field anyway.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          form.setFocus('title');
        }}>
        <DialogHeader>
          <DialogTitle className="text-[25px] font-bold text-[#12282A]">{isEdit ? 'Lektion bearbeiten' : 'Neue Lektion'}</DialogTitle>
        </DialogHeader>

        {/* The sidebar's provider does not reach here — the dialog is a sibling in the React tree. */}
        <TooltipProvider delayDuration={200}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col flex-1 min-h-0 gap-4">
              {/* min-h-0 so this can shrink below its content — without it a flex child refuses to
                  shrink and pushes the footer out of the dialog instead. No `items-start`: the columns
                  have to stretch to the row height, otherwise the preview cannot fill it. */}
              <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
                {/* Fields scroll on their own; pr-1 keeps the scrollbar off the inputs. */}
                <div className="flex flex-col gap-5 min-w-0 overflow-y-auto px-2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[10px]">
                        <LabelWithHint hint="Wird in der Übersicht und im Inhaltsverzeichnis angezeigt.">Name der Lektion *</LabelWithHint>
                        <FormControl>
                          <Input
                            {...field}
                            onBlur={() => {
                              field.onBlur();
                              prefillHeadingFromTitle();
                            }}
                            placeholder="z. B. Begrüßung"
                            disabled={isBusy}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="heading"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[10px]">
                        <LabelWithHint hint="Steht über dem Inhalt der Lektion — darf länger und ausführlicher sein.">Überschrift *</LabelWithHint>
                        <FormControl>
                          <Input {...field} placeholder="Wird aus dem Namen übernommen" disabled={isBusy} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="videoUrl"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[10px]">
                        <FormLabel className="text-[14px] font-bold text-[#12282A]">Video-URL</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://vimeo.com/123456789" disabled={isBusy} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="textHtml"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[10px]">
                        <FormLabel className="text-[14px] font-bold text-[#12282A]">Text</FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Transkript oder Inhalt der Lektion…"
                            disabled={isBusy}
                            onImageUpload={onImageUpload}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Fixed in place — LessonView scrolls its own body under the breadcrumb. */}
                <div className="flex flex-col min-w-0 min-h-0">
                  {/* The real learner component — a rebuilt preview would drift the moment LessonView changes. */}
                  <LessonView
                    moduleTitle={moduleTitle}
                    lessonTitle={watched.title || 'Neue Lektion'}
                    lesson={{
                      heading: watched.heading || watched.title || 'Überschrift der Lektion',
                      videoUrl: watched.videoUrl?.trim() || null,
                      textHtml: hasEditorContent(watched.textHtml) ? watched.textHtml : null
                    }}
                    variant="preview"
                    fillHeight
                  />
                </div>
              </div>

              <DialogFooter className="flex gap-3 shrink-0">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={isBusy}>
                  {isSubmitting ? 'Speichert…' : isEdit ? 'Speichern' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
};

export default LessonFormDialog;
